/**
 * @fileoverview Sticky two-row header of the student matrix — same shape as the teacher's: lesson
 * columns on top, self / teacher / deliverable sub-columns below, then the Final Assessment pair.
 */

import { Fragment } from 'react'
import { Stack, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import type { StudentTrackerLesson } from '../types'
import { formatDate } from './formatDate'
import {
  FINAL_SUBCOLS,
  headerCellStyle,
  stickyHeaderStyle,
  stickyTheadStyle,
  subColCount,
  subHeaderStyle,
} from './matrixStyles'

const HEADER_LINE_GAP = 4

interface StudentTrackerMatrixHeadProps {
  lessons: StudentTrackerLesson[]
}

export function StudentTrackerMatrixHead({ lessons }: StudentTrackerMatrixHeadProps) {
  const { t, i18n } = useTranslation()
  const tr = (key: string) => t(`student_task_tracker.table.${key}`)

  return (
    <thead style={stickyTheadStyle}>
      <tr>
        <th rowSpan={2} style={stickyHeaderStyle}>
          <Text size='sm'>{tr('student')}</Text>
        </th>
        {lessons.map(lesson => {
          const cols = subColCount(lesson)
          if (cols === 0) return null
          return (
            <th key={lesson.id} colSpan={cols} style={headerCellStyle}>
              <Stack spacing={HEADER_LINE_GAP}>
                {lesson.date && (
                  <Text size='xs' color='dimmed'>
                    {formatDate(lesson.date, i18n.language)}
                  </Text>
                )}
                <Text size='sm' weight={600} lineClamp={2}>
                  {lesson.title}
                </Text>
              </Stack>
            </th>
          )
        })}
        <th colSpan={FINAL_SUBCOLS} style={headerCellStyle}>
          <Stack spacing={HEADER_LINE_GAP}>
            <Text size='xs' color='dimmed' lineClamp={1}>
              &nbsp;
            </Text>
            <Text size='sm' weight={600} lineClamp={2}>
              {tr('final')}
            </Text>
          </Stack>
        </th>
      </tr>
      <tr>
        {lessons.map(lesson => {
          const showActivityName = lesson.deliverableActivities.length > 1
          return (
            <Fragment key={lesson.id}>
              {lesson.hasAssessments && (
                <>
                  <th style={subHeaderStyle}>{tr('self_assessment')}</th>
                  <th style={subHeaderStyle}>{tr('teacher_assessment')}</th>
                </>
              )}
              {lesson.deliverableActivities.map(activity => (
                <th key={activity.id} style={subHeaderStyle}>
                  <Stack spacing={HEADER_LINE_GAP} align='center'>
                    <Text size='xs' color='dimmed'>
                      {tr('deliverable')}
                    </Text>
                    {showActivityName && (
                      <Text size='xs' weight={500} lineClamp={2}>
                        {activity.title}
                      </Text>
                    )}
                  </Stack>
                </th>
              ))}
            </Fragment>
          )
        })}
        <th style={subHeaderStyle}>{tr('self_assessment')}</th>
        <th style={subHeaderStyle}>{tr('teacher_assessment')}</th>
      </tr>
    </thead>
  )
}
